// src/utils/pipelineStageDraft.js
// SEAM 4 VERIFIER: qpipe-4d1a7c35-draft
//
// Pure helpers for the Settings → Pipeline editor's UNSAVED draft. No React,
// no Firebase, no imports — functions/scripts/testQuotePipeline.js evaluates
// this file directly, so keep it dependency-free.
//
// The editor keeps a BASE (the saved list + revision the edits started from)
// separately from the live server list, so:
//   - a deleted row is only MARKED (pendingDelete) until Save — nothing
//     vanishes before it is actually saved;
//   - "unsaved changes" is derived by comparing the draft to the base, so
//     undoing an edit clears it;
//   - Save sends the BASE revision, so a change saved elsewhere meanwhile is
//     refused by the server instead of silently overwritten.

// Mirrors STAGE_DELETE_OCCUPANT_CAP in functions/utils/quotePipeline.js (the
// server enforces it; this only lets the screen warn before calling).
// testQuotePipeline.js fails if the two differ.
export const PIPELINE_DELETE_OCCUPANT_CAP = 100;

const TEMPLATE_PREFIX = 'tmpl_';
let tempSeq = 0;

export function draftRowsFrom(stages) {
  return (stages || []).map((s) => ({
    key: s.id,
    id: s.id,
    label: s.label,
    type: s.type,
    isSystem: !!s.isSystem,
    isNew: false,
    pendingDelete: false,
    staleInput: s.staleAfterDays == null ? '' : String(s.staleAfterDays),
  }));
}

export function newDraftRow(label, type) {
  tempSeq += 1;
  return { key: `new_${tempSeq}`, id: null, label, type, isSystem: false, isNew: true, pendingDelete: false, staleInput: '' };
}

const staleOf = (v) => (v === '' || v == null ? null : Number(v));

// What the draft changes relative to the base list. Every array holds
// human-readable facts the screen can list back to the user.
export function pendingChanges(baseStages, rows) {
  const base = baseStages || [];
  const byId = new Map(base.map((s) => [s.id, s]));
  const out = { added: [], deleted: [], renamed: [], staleChanged: [], reordered: false };

  for (const r of rows || []) {
    if (r.isNew) {
      if (!r.pendingDelete) out.added.push(r.label.trim() || '(unnamed)');
      continue;
    }
    const b = byId.get(r.id);
    if (!b) continue;
    if (r.pendingDelete) { out.deleted.push(b.label); continue; }
    if (r.label.trim() !== b.label) out.renamed.push({ from: b.label, to: r.label.trim() || '(unnamed)' });
    const was = b.staleAfterDays == null ? null : b.staleAfterDays;
    const now = staleOf(r.staleInput);
    if (was !== now) out.staleChanged.push({ label: r.label.trim() || b.label, from: was, to: now });
  }

  const keptIds = (rows || []).filter((r) => !r.isNew && !r.pendingDelete).map((r) => r.id);
  const baseOrder = base.map((s) => s.id).filter((id) => keptIds.includes(id));
  out.reordered = keptIds.join('|') !== baseOrder.join('|');
  return out;
}

export function hasPendingChanges(changes) {
  return !!changes && (changes.added.length > 0 || changes.deleted.length > 0 || changes.renamed.length > 0
    || changes.staleChanged.length > 0 || changes.reordered);
}

const days = (v) => (v == null ? 'no limit' : `${v} day${v === 1 ? '' : 's'}`);

export function summarizePendingChanges(changes) {
  if (!changes) return [];
  const lines = [];
  for (const l of changes.added) lines.push(`Added "${l}"`);
  for (const l of changes.deleted) lines.push(`Deleted "${l}"`);
  for (const r of changes.renamed) lines.push(`Renamed "${r.from}" to "${r.to}"`);
  for (const s of changes.staleChanged) lines.push(`"${s.label}" stale after: ${days(s.from)} → ${days(s.to)}`);
  if (changes.reordered) lines.push('Changed the order of stages');
  return lines;
}

// Ids of SAVED stages marked for deletion (a never-saved new row can hold no
// estimates, and template placeholders are never on a quote).
export function removedStageIds(rows) {
  return (rows || []).filter((r) => r.pendingDelete && !r.isNew && r.id && !r.id.startsWith(TEMPLATE_PREFIX)).map((r) => r.id);
}

// What goes to savePipelineStages: rows marked for deletion are left out.
export function saveInputFrom(rows) {
  return (rows || []).filter((r) => !r.pendingDelete).map((r) => ({
    id: r.isNew ? null : r.id,
    label: r.label,
    type: r.type,
    staleAfterDays: staleOf(r.staleInput),
  }));
}

// countsById must be FRESH counts for the removed ids (not a cached display).
export function occupancyDecision(removedIds, countsById, labelsById, cap = PIPELINE_DELETE_OCCUPANT_CAP) {
  const perStage = removedIds
    .map((id) => ({ id, label: (labelsById && labelsById[id]) || id, count: Number((countsById || {})[id]) || 0 }))
    .filter((x) => x.count > 0);
  const total = perStage.reduce((s, x) => s + x.count, 0);
  return { perStage, total, overCap: total > cap, needsConfirm: total > 0 && total <= cap };
}

// The server refuses a save built on an out-of-date list with
// failed-precondition ("Someone else changed the stages…" / "…changed
// somewhere else…"). Anything else is an ordinary error.
export function isStaleListError(err) {
  const code = String((err && err.code) || '');
  const msg = String((err && err.message) || '');
  return /failed-precondition/.test(code) && /someone else|changed somewhere else/i.test(msg);
}
